/** Minimal inline stroke icon set (24x24 viewBox, currentColor). */
import type { SVGProps } from 'react';
import type { MethodKind, VesselKind } from '@shared/schemas';

export type IconName =
  | 'home' | 'coffee' | 'bean' | 'history' | 'gear' | 'settings' | 'scale' | 'plus' | 'minus'
  | 'bluetooth' | 'battery' | 'timer' | 'check' | 'x' | 'chevron-right' | 'chevron-left' | 'chevron-down'
  | 'play' | 'stop' | 'pause' | 'trash' | 'edit' | 'refresh' | 'alert' | 'info' | 'star' | 'star-half'
  | 'download' | 'upload' | 'archive' | 'copy' | 'chart' | 'droplet' | 'flame' | 'grinder' | 'cup'
  | 'portafilter' | 'pitcher' | 'zero' | 'search' | 'filter' | 'more' | 'lock' | 'link' | 'compare' | 'sparkles'
  | 'moon' | 'camera' | 'arrow-up' | 'arrow-down' | 'speaker'
  | 'dripper' | 'french-press' | 'aeropress' | 'moka' | 'cold-brew' | 'carafe' | 'dosing-cup' | 'machine';

const paths: Record<IconName, React.ReactNode> = {
  'arrow-up': <><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></>,
  'arrow-down': <><path d="M12 5v14" /><path d="m19 12-7 7-7-7" /></>,
  speaker: <><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" /><path d="M15.5 9a4 4 0 0 1 0 6" /><path d="M18 6.5a7.5 7.5 0 0 1 0 11" /></>,
  home: <><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /><path d="M10 21v-6h4v6" /></>,
  coffee: <><path d="M4 8h13v5a6 6 0 0 1-6 6h-1a6 6 0 0 1-6-6V8Z" /><path d="M17 10h1.5a2.5 2.5 0 0 1 0 5H17" /><path d="M8 2.5c0 1 1 1.5 1 2.5s-1 1.5-1 2.5M12 2.5c0 1 1 1.5 1 2.5s-1 1.5-1 2.5" /></>,
  bean: <><ellipse cx="12" cy="12" rx="6.5" ry="9" transform="rotate(35 12 12)" /><path d="M8.5 18.5c1-3 4.5-4 5.5-7s0-5.5-.5-6.5" /></>,
  history: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l3 2" /></>,
  gear: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" /></>,
  settings: <><path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0" /><circle cx="16" cy="6" r="2" /><circle cx="10" cy="12" r="2" /><circle cx="18" cy="18" r="2" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  bluetooth: <path d="m7 7 10 10-5 5V2l5 5L7 17" />,
  battery: <><rect x="2" y="7" width="17" height="10" rx="2" /><path d="M22 11v2" /></>,
  timer: <><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2M10 2h4" /></>,
  check: <path d="M20 6 9 17l-5-5" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  'chevron-right': <path d="m9 18 6-6-6-6" />,
  'chevron-left': <path d="m15 18-6-6 6-6" />,
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  play: <path d="M7 4v16l13-8L7 4Z" />,
  stop: <rect x="6" y="6" width="12" height="12" rx="1.5" />,
  pause: <path d="M8 5v14M16 5v14" />,
  trash: <><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /><path d="M10 11v6M14 11v6" /></>,
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
  refresh: <><path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" /><path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" /><path d="M21 3v5h-5M3 21v-5h5" /></>,
  alert: <><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></>,
  star: <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9L12 3Z" />,
  'star-half': <><path d="M12 3v14.3L6.5 20.2l1-6.2L3 9.6l6.2-.9L12 3Z" fill="currentColor" /><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3" /></>,
  download: <><path d="M12 3v12M7 10l5 5 5-5" /><path d="M4 21h16" /></>,
  upload: <><path d="M12 21V9M7 14l5-5 5 5" /><path d="M4 3h16" /></>,
  archive: <><rect x="3" y="4" width="18" height="5" rx="1" /><path d="M5 9v11h14V9M10 13h4" /></>,
  copy: <><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" /></>,
  chart: <><path d="M3 3v18h18" /><path d="m7 15 4-5 3 3 5-7" /></>,
  droplet: <path d="M12 2.7S5 10 5 14.5a7 7 0 0 0 14 0C19 10 12 2.7 12 2.7Z" />,
  flame: <path d="M12 2s5 4.5 5 10a5 5 0 0 1-10 0c0-2 1-3.5 1-3.5S9 11 10.5 11C10.5 7 12 2 12 2Z" />,
  cup: <><path d="M5 8h12v6a5 5 0 0 1-5 5h-2a5 5 0 0 1-5-5V8Z" /><path d="M17 10h1a2.5 2.5 0 0 1 0 5h-1" /><path d="M3 21h16" /></>,
  // Coffee gear, drawn from the side (#103).
  grinder: <><path d="M12 6V3.5h6" /><circle cx="19.2" cy="3.5" r="1.2" /><path d="M10.5 7.5V6h3v1.5" /><rect x="8" y="7.5" width="8" height="14" rx="2" /><path d="M8 10.5h8M8 16.5h8" /></>,
  portafilter: <><path d="M1.5 7.7h12" /><path d="m2.8 7.7.9 4.7a1 1 0 0 0 1 .8h5.6a1 1 0 0 0 1-.8l.9-4.7" /><path d="M12 8.4h7.9a1.8 1.8 0 0 1 0 3.6l-8.3-.9" /><path d="m5.9 13.2.7 1.8h1.8l.7-1.8" /><path d="m6.6 15-.5 1.3M8.4 15l.5 1.3" /></>,
  'dosing-cup': <><path d="M5.5 3.5h13" /><path d="M6.5 3.5v14a3 3 0 0 0 3 3h5a3 3 0 0 0 3-3v-14" /><ellipse cx="12" cy="14" rx="2.6" ry="3.6" transform="rotate(35 12 14)" /><path d="M10.6 16.6c.4-1.2 1.8-1.6 2.2-2.8s0-2.2-.2-2.6" /></>,
  pitcher: <><path d="M6.5 5.5h9l1.5 13.5a2 2 0 0 1-2 2.2H7a2 2 0 0 1-2-2.2Z" /><path d="M6.5 5.5 4.2 3.5" /><path d="M16.2 8.5h1.6a1.8 1.8 0 0 1 1.8 1.8v3.9a1.8 1.8 0 0 1-1.8 1.8h-1" /></>,
  dripper: <><path d="M3 6h18" /><path d="M4.5 6 10 15.5h4L19.5 6" /><path d="M10 15.5V18M14 15.5V18" /><path d="M6.5 18h11" /></>,
  'french-press': <><path d="M11 6.5v-4M9 2.5h4" /><path d="M4.5 6.5h13" /><path d="M5.5 6.5v13a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-13" /><path d="M5.5 12.5h11" /><path d="M16.5 9H18a1.8 1.8 0 0 1 1.8 1.8v5.4A1.8 1.8 0 0 1 18 18h-1.5" /></>,
  aeropress: <><path d="M7.5 3.5h9" /><path d="M9 3.5V8M15 3.5V8" /><path d="M7.5 8h9v10.5h-9Z" /><path d="M10 11.5h2M10 14.5h2" /><path d="M4 18.5h16" /><path d="M8 18.5v2h8v-2" /></>,
  moka: <><path d="M11.5 4V2.5" /><path d="M6.5 4h10l-2 7.5h-6Z" /><path d="M6.5 4 4.5 3" /><path d="M8.5 11.5h6l2.5 10H6Z" /><path d="M16.2 5.2h3.3l-2.2 5" /></>,
  'cold-brew': <><rect x="6.5" y="2.5" width="11" height="3.5" rx="1" /><path d="M6.5 6h11v13a2.5 2.5 0 0 1-2.5 2.5H9A2.5 2.5 0 0 1 6.5 19Z" /><rect x="8.6" y="12.5" width="3.6" height="3.6" rx=".6" transform="rotate(-12 10.4 14.3)" /><rect x="12" y="15.4" width="3.6" height="3.6" rx=".6" transform="rotate(10 13.8 17.2)" /></>,
  carafe: <><rect x="7" y="3.8" width="7" height="3.4" rx=".8" /><path d="M7 4.4 5.3 3.4" /><path d="M7.6 7.2C4.3 8.6 2.8 11.4 2.8 14.4c0 2.5 1 4.6 2.6 6.1h10.2c1.6-1.5 2.6-3.6 2.6-6.1 0-3-1.5-5.8-4.8-7.2" /><path d="M3.1 13.2h14.8" /><path d="M14 5h4.6a2 2 0 0 1 2 2v4.2a2 2 0 0 1-.9 1.7l-1.6 1" /></>,
  // The coffee machine, from the front: from the side its outline reads as a letter. A gauge, the group head with two
  // spouts, and a cup on the drip tray.
  machine: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M4 8h16" /><circle cx="15.8" cy="5.5" r="1" /><path d="M7.2 5.5h3" /><path d="M9.5 8v1.8h5V8" /><path d="M11 9.8v1.2M13 9.8v1.2" /><path d="M9.5 13.5h5v1.3a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2Z" /><path d="M7 18.5h10" /></>,
  // The scale, from above (#279): from the side it's a bare slab. The pad with a bean on it, the display at the front.
  // The bean's crease stops short of its outline, which it would merge into at this size.
  scale: <><rect x="3" y="3" width="18" height="18" rx="2.5" /><ellipse cx="12" cy="9.5" rx="3.3" ry="4.4" transform="rotate(35 12 9.5)" /><path d="M10.6 10.8c1.7-.3 1.1-2.3 2.8-2.6" /><path d="M3 16h18" /><path d="M13.5 18.5h4" /></>,
  zero: <><ellipse cx="12" cy="12" rx="6" ry="8" /><path d="M8 17 16 7" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  filter: <path d="M3 5h18l-7 8v6l-4 2v-8L3 5Z" />,
  more: <><circle cx="5" cy="12" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /></>,
  lock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
  link: <><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></>,
  compare: <><path d="M8 3v18M16 3v18" /><path d="m4 7 4-4 4 4M12 17l4 4 4-4" /></>,
  sparkles: <><path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8L12 3Z" /><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15Z" /></>,
  moon: <path d="M20.5 14.6A8.5 8.5 0 1 1 9.4 3.5a7 7 0 0 0 11.1 11.1Z" />,
  camera: <><path d="M3 8.5A2 2 0 0 1 5 6.5h2.2L9 4h6l1.8 2.5H19a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8.5Z" /><circle cx="12" cy="13" r="3.5" /></>,
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 20, strokeWidth = 1.8, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {paths[name]}
    </svg>
  );
}

/** One icon per vessel kind, the same in the wizard, on the Scale page, in Gear › Vessels and in the simulator. */
export const VESSEL_ICONS: Record<VesselKind, IconName> = {
  grinder: 'grinder',
  dosing_cup: 'dosing-cup',
  portafilter: 'portafilter',
  brewer: 'dripper',
  cup: 'cup',
  carafe: 'carafe',
  pitcher: 'pitcher',
  other: 'scale',
};

export const METHOD_ICONS: Record<MethodKind, IconName> = {
  espresso: 'portafilter',
  pourover: 'dripper',
  immersion: 'french-press',
  aeropress: 'aeropress',
  moka: 'moka',
  coldbrew: 'cold-brew',
  other: 'coffee',
};
